import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { MapPin, ArrowRight } from "lucide-react";

const TIMELINE = [
  {
    year: "೧೪೩೦",
    yearRoman: "1430",
    title: "Kumaravyasa composes the Gadugina Bharata",
    body: "At the Veeranarayana temple in Gadag, Kumaravyasa completes the Karnata Bharata Kathamanjari — Karnataka's most beloved retelling of the Mahabharata.",
  },
  {
    year: "೧೮೯೨",
    yearRoman: "1892",
    title: "Ganayogi Panchakshari Gavai's school for blind children",
    body: "In Gadag, the blind saint-musician Panchakshari Gavai establishes one of the first residential music and academic schools for visually-impaired children in India.",
  },
  {
    year: "೨೦೨೬",
    yearRoman: "2026",
    title: "Akshara Daana arrives in Gadag",
    body: "The flag rose in July 2026. Professors, educationists, doctors and public figures gathered to open our new chapter in a land that has taught India for six centuries.",
  },
  {
    year: "೩೧.೦೮",
    yearRoman: "31-aug-2026",
    sub: "31 August 2026",
    title: "Field visit to Lakshmeshwar school",
    body: "Smt. Revathi Kamath, Smt. Anitha Dasappa, Shri Manu C P and Dr. Abhinandan visit the Government Model Primary School at Lakshmeshwar with local supporters Shri Poojar, Shri Girish Agadi and Principal Shri Doddamani. A fallen compound wall, no benches, no proper toilets, unpainted walls and no washing units — and children hopeful that their school is taken up next.",
    link: "/news#lakshmeshwar-visit",
    linkLabel: "Read the field report",
  },
];

export default function Gadag() {
  const ref = useReveal();

  return (
    <section id="gadag" data-testid="gadag-section" className="relative py-16 md:py-32 bg-parchment overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal max-w-3xl text-center md:text-left mx-auto md:mx-0">
          <div className="flex items-center justify-center md:justify-start gap-3 flex-wrap mb-4">
            <span className="badge-pulse" data-testid="gadag-badge">
              <span className="w-2 h-2 rounded-full bg-vermilion" />
              Gadag Chapter · Launched July 2026
            </span>
            <span className="inline-flex items-center gap-1.5 text-indigo/70 font-display text-sm">
              <MapPin className="w-4 h-4 text-vermilion" /> Gadag, North Karnataka
            </span>
          </div>
          <h2 className="font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight">
            A town that has taught India for six centuries — now home to our newest chapter.
          </h2>
          <p className="mt-4 text-indigo/80 font-display text-base md:text-lg leading-relaxed">
            Gadag is not new to learning. It has been a seat of poetry, music and rigorous instruction long
            before us. Akshara Daana opened its Gadag chapter in July 2026, with professors, educationists,
            doctors and public figures standing with us. The town&rsquo;s heritage holds the door open.
          </p>
        </div>

        <div className="mt-12 md:mt-16 relative">
          <div className="absolute left-[7px] md:left-1/2 top-4 bottom-4 w-[2px] border-l-2 border-dashed border-vermilion/50" aria-hidden />
          <ol className="space-y-10 md:space-y-12">
            {TIMELINE.map((t, i) => (
              <li
                key={t.yearRoman}
                data-testid={`gadag-timeline-${t.yearRoman}`}
                className={`relative pl-8 md:pl-0 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 items-start ${
                  i % 2 === 0 ? "md:[&>div:first-child]:order-1" : ""
                }`}
              >
                <span className="md:hidden absolute left-0 top-3 w-4 h-4 rounded-full bg-turmeric border-[3px] border-parchment shadow" aria-hidden />
                <div className={`relative min-w-0 ${i % 2 === 0 ? "md:text-right" : ""}`}>
                  <span className="hidden md:block absolute top-3 w-4 h-4 rounded-full bg-turmeric border-4 border-parchment shadow"
                        style={i % 2 === 0 ? { left: "-44px" } : { right: "-44px" }} />
                  <div className="stat-num text-4xl sm:text-5xl md:text-6xl text-turmeric leading-none">{t.year}</div>
                  <div className="text-indigo/50 font-display text-xs uppercase tracking-widest mt-1">{t.sub || `${t.yearRoman} CE`}</div>
                </div>
                <div className="manuscript-card min-w-0">
                  <h3 className="font-yatra text-xl md:text-2xl text-indigo leading-snug">{t.title}</h3>
                  <p className="mt-2 text-indigo/75 font-display text-sm md:text-base leading-relaxed">{t.body}</p>
                  {t.link && (
                    <Link to={t.link} data-testid={`gadag-timeline-link-${t.yearRoman}`} className="mt-4 inline-flex items-center gap-1.5 text-teal font-display font-bold text-sm hover:underline">
                      {t.linkLabel} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-6">
          {[
            { k: "Professors", v: "Academic guidance from Karnataka's top universities" },
            { k: "Doctors", v: "A rural school health partnership" },
            { k: "Public Figures", v: "Voices from music, cinema and civic life" },
          ].map((x) => (
            <div key={x.k} className="border-l-4 border-teal pl-5 py-2">
              <div className="font-display font-bold text-navy text-xl">{x.k}</div>
              <div className="mt-1 text-indigo/70 font-display text-sm">{x.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
